import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/base/ui/dropdownMenu'
import {ScrollArea} from '@/base/ui/scrollArea'
import {cn} from '@/lib/utils'
import React from 'react'

export interface IWuMenuProps
  extends Omit<
    React.ComponentProps<typeof DropdownMenu>,
    'open' | 'onOpenChange'
  > {
  name?: string
  menuTitle?: string
  Trigger?: React.ReactElement<HTMLBaseElement>
  variant?: 'flat' | 'outlined'
  open?: boolean
  onOpenChange?: (open: boolean) => void
  height?: string
  preventCloseOnClickOutside?: boolean
  dir?: 'ltr' | 'rtl'
  align?: 'start' | 'center' | 'end'
  alignOffset?: number
  side?: 'left' | 'right' | 'top' | 'bottom'
  sideOffset?: number
  className?: string
  disabled?: boolean
}

const TriggerClass =
  'p-1.5 cursor-pointer hover:bg-gray-20 border-gray-40 transition duration-300 focus:outline-2 focus:outline-offset focus:outline-blue-p/80 focus:bg-gray-20 hover:bg-gray-20 rounded-t-sm'
export const WuMenu: React.FC<IWuMenuProps> = props => {
  const {
    children,
    menuTitle,
    variant = 'flat',
    name,
    open,
    onOpenChange,
    height,
    preventCloseOnClickOutside,
    dir,
    Trigger,
    align = 'start',
    sideOffset = 0,
    side = 'bottom',
    alignOffset,
    className,
    disabled,
    ...rest
  } = props
  return (
    <DropdownMenu {...rest} open={open} onOpenChange={onOpenChange} dir={dir}>
      <DropdownMenuTrigger
        asChild
        dir={dir}
        disabled={disabled}
        className={cn(
          'disabled:text-zinc-300 disabled:bg-white disabled:cursor-not-allowed disabled:hover:bg-transparent',
        )}
      >
        {Trigger ?? defaultMenuTrigger(variant, name, disabled)}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          'w-56 border border-blue-p disabled:border-gray-40',
          className,
          'wu-menu-content',
        )}
        align={align}
        sideOffset={sideOffset}
        side={side}
        alignOffset={alignOffset}
        onInteractOutside={e =>
          preventCloseOnClickOutside && e.preventDefault()
        }
      >
        <ScrollArea className="h-fit" style={{height}} dir={dir}>
          {menuTitle && (
            <>
              <DropdownMenuLabel dir={dir} className="wu-menu-title">
                {menuTitle}
              </DropdownMenuLabel>
              <DropdownMenuSeparator
                className="mt-0 wu-menu-separator"
                dir={dir}
              />
            </>
          )}
          {children ?? <p>Menu Is Empty</p>}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

const defaultMenuTrigger = (
  variant: 'flat' | 'outlined',
  name?: string,
  disabled?: boolean,
): React.JSX.Element => {
  const className = cn(
    TriggerClass,
    'flex items-center justify-between gap-2 h-8 text-xs group',
    variant === 'outlined' ? 'border' : 'border-b',
    'wu-trigger-btn',
  )
  return (
    <button className={className} aria-label="menu-trigger">
      <span>{name ?? 'Menu'}</span>
      <span
        className={cn(
          'wm-arrow-drop-down text-gray-lead text-base rotate-180 group-data-[state=closed]:rotate-0 transition-transform duration-300 group-hover:text-blue-q',
          disabled && 'text-zinc-300 group-hover:text-zinc-300',
        )}
      ></span>
    </button>
  )
}
